import FeatureGuardLogic from "App/FeatureGuardLogic";
import RoleGuard from "components/RoleGuard";
import ACCOUNT_FEATURE from "enums/ACCOUNT_FEATURE";
import { APP_ROUTES } from "enums/APP_ROUTES";
import ROLE from "enums/ROLE_NAME";
import AppLayout from "features/AppLayout";

import TransitionalRegion from "components/TransitionalRegion";
import ScriptListing from "features/ScriptManagement";
import { lazy, Suspense } from "react";
import { Navigate, Outlet, Route } from "react-router-dom";

const LayoutDashboard = lazy(() => import("components/LayoutDashboard"));

const ProjectManagement = lazy(() => import("features/ProjectManagement"));
const AccountDashboard = lazy(
  () => import("features/AccountManagement/components/AccountDashboard"),
);
const AccountSettings = lazy(
  () => import("features/AccountManagement/components/AccountSettings"),
);
const AccountsListing = lazy(() => import("features/AccountManagement"));
const AdminListing = lazy(() => import("features/AdminManagement"));
const AdminAdd = lazy(() => import("features/AdminManagement/ViewAdd"));

const ScriptDetails = lazy(
  () => import("features/ScriptManagement/ScriptDetails"),
);
const ScriptAdd = lazy(() => import("features/ScriptManagement/ViewAdd"));

const PanelManagement = lazy(() => import("features/PanelManagement"));

const ParticipantListing = lazy(() => import("features/ParticipantManagement"));
const ParticipantImports = lazy(
  () => import("features/ParticipantManagement/ParticipantImports"),
);
const ParticipantGift = lazy(
  () => import("features/ParticipantManagement/ViewGift"),
);
const ParticipantInvite = lazy(
  () => import("features/ParticipantManagement/ViewInvite"),
);
const ParticipantProfile = lazy(
  () => import("features/ParticipantManagement/ViewProfile"),
);

const PlaylistListing = lazy(
  () => import("features/PlaylistManagement/PlaylistListing"),
);
const PlaylistDetails = lazy(
  () => import("features/PlaylistManagement/PlaylistDetails"),
);
const PlaylistAdd = lazy(() => import("features/PlaylistManagement/ViewAdd"));

const DocumentManagement = lazy(() => import("features/DocumentManagement"));

const VideoListing = lazy(
  () => import("features/VideoManagement/VideoListing"),
);
const VideoReview = lazy(() => import("features/VideoManagement/VideoReview"));
const ViewUpload = lazy(() => import("features/VideoManagement/VideoUpload"));

const SyntheticPersonasPage = lazy(() => import("features/SyntheticPersonas"));
const SemanticSearch = lazy(() => import("features/SemanticSearch"));

const SuspenseWrapper: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <Suspense fallback={<TransitionalRegion noResultsText={<></>} />}>
    {children}
  </Suspense>
);

const SuperAdminRoutes = (
  <>
    <Route
      element={
        <SuspenseWrapper>
          <LayoutDashboard />
        </SuspenseWrapper>
      }
    >
      <Route path="dashboard" element={<ProjectManagement />} />
      <Route path="dashboard/:activeId" element={<ProjectManagement />} />
      <Route
        path="self-service-accounts"
        element={
          <RoleGuard allowedRoles={[ROLE.SUPER_DUPER_ADMIN]}>
            <AccountsListing selfService />
          </RoleGuard>
        }
      />
      <Route
        path="accounts"
        element={
          <RoleGuard allowedRoles={[ROLE.SUPER_DUPER_ADMIN]}>
            <AccountsListing />
          </RoleGuard>
        }
      />
      <Route path="account/:accountId" element={<AccountDashboard />} />
      <Route path="account/:accountId/settings" element={<AccountSettings />} />
      <Route path="search" element={<SemanticSearch />} />
    </Route>

    <Route
      path="project"
      element={
        <SuspenseWrapper>
          <Outlet />
        </SuspenseWrapper>
      }
    >
      <Route index element={<Navigate to="/dashboard" />} />

      <Route path=":slug" element={<AppLayout />}>
        <Route index element={<Navigate to={APP_ROUTES.SCRIPTS} />} />

        <Route path={APP_ROUTES.ADMINS} element={<AdminListing />} />
        <Route path={`${APP_ROUTES.ADMINS}/add`} element={<AdminAdd />} />

        <Route path={`${APP_ROUTES.SCRIPTS}/add`} element={<ScriptAdd />} />
        <Route path={APP_ROUTES.SCRIPTS} element={<ScriptListing />}>
          <Route path=":activeId" element={<ScriptDetails />} />
        </Route>

        <Route
          path={`${APP_ROUTES.PARTICIPANTS}/add`}
          element={<ParticipantImports />}
        />
        <Route path={APP_ROUTES.PARTICIPANTS} element={<ParticipantListing />}>
          <Route path="invite" element={<ParticipantInvite />} />
          {/* TODO: Revisit using a role-specifiable AuthVidletAdminLogic instead of having multiple route files per role */}
          {/* <Route element={<AuthVidletAdminLogic />}>
            <Route path="gift" element={<ViewParticipantsGift />} />
          </Route> */}
          <Route path="gift" element={<ParticipantGift />} />
          <Route path="profile" element={<ParticipantProfile />} />
        </Route>

        <Route path={APP_ROUTES.PLAYLISTS} element={<PlaylistListing />} />
        <Route path={`${APP_ROUTES.PLAYLISTS}/add`} element={<PlaylistAdd />} />
        <Route
          path={`${APP_ROUTES.PLAYLISTS}/:activeId`}
          element={<PlaylistDetails />}
        />

        <Route path={APP_ROUTES.DOCUMENTS} element={<DocumentManagement />} />

        <Route path={`${APP_ROUTES.VIDEOS}/upload`} element={<ViewUpload />} />
        <Route
          path={`${APP_ROUTES.VIDEOS}/:listingType`}
          element={<VideoListing />}
        />
        <Route
          path={`${APP_ROUTES.VIDEOS}/:listingType/review`}
          element={<VideoReview />}
        />
        <Route
          path={`${APP_ROUTES.VIDEOS}/:listingType/review/:activeId`}
          element={<VideoReview />}
        />

        <Route path={APP_ROUTES.HYDRA} element={<PanelManagement />} />

        <Route
          element={<FeatureGuardLogic featureName={ACCOUNT_FEATURE.PERSONAS} />}
        >
          <Route
            path={APP_ROUTES.PERSONAS}
            element={<SyntheticPersonasPage />}
          />
        </Route>
      </Route>
    </Route>
  </>
);

export default SuperAdminRoutes;
